import { AxiosResponse } from 'axios';

import { AssetScope, AssetType } from '@/enums';
import { BlobLocation } from '@/enums/BlobLocationEnum';
import {
  buildUploadSasPayload,
  prepareUploadFile,
  uploadFileWithSasToken,
  validateUploadFile
} from '@/helpers/UploadFileHelper';
import { AssetUploadErrorType, ThumbnailUploadErrorResponse, ThumbnailUploadPayload, UploadErrorDetail } from '@/types';

type CreateSasTokenFn = (
  payload: ReturnType<typeof buildUploadSasPayload>
) => Promise<AxiosResponse<ThumbnailUploadPayload, ThumbnailUploadErrorResponse> | undefined>;

export type CoreUploadResult =
  | {
      assetUrl: string;
      fileToUpload: File;
      duration?: number;
      assetType: AssetType;
      sasAssetName: string;
      error?: never;
    }
  | { error: UploadErrorDetail; assetUrl?: never };

export type CoreUploadContext = {
  validationFormats: string[];
  uploadAssetType?: AssetType | string;
  assetScope?: AssetScope;
  eventId?: string | number | null;
  organizationId?: string | number | null;
  isPublic?: boolean;
  createSasToken: CreateSasTokenFn;
};

export const coreUpload = async (
  file: File,
  context: CoreUploadContext,
  sasOptions?: { blobLocation?: BlobLocation; themeId?: number | null }
): Promise<CoreUploadResult> => {
  const { validationFormats, uploadAssetType, assetScope, eventId, organizationId, isPublic, createSasToken } = context;

  const validationResult = validateUploadFile({ file, validationFormats, uploadAssetType });

  if (validationResult.errorType || validationResult.assetType === undefined) {
    return {
      error: {
        assetName: file.name,
        errorType: validationResult.errorType ?? AssetUploadErrorType.GENERIC_UPLOAD_ERROR
      }
    };
  }

  const assetType = validationResult.assetType;
  const { fileToUpload, duration, errorType } = await prepareUploadFile(file, assetType);

  if (errorType || !fileToUpload) {
    return {
      error: {
        assetName: file.name,
        errorType: errorType ?? AssetUploadErrorType.GENERIC_UPLOAD_ERROR
      }
    };
  }

  const sasTokenData = await createSasToken(
    buildUploadSasPayload({
      assetScope,
      eventId,
      organizationId,
      isPublic,
      file: fileToUpload,
      isThumbnail: false,
      ...sasOptions
    })
  );

  if (!sasTokenData) {
    return {
      error: { assetName: file.name, errorType: AssetUploadErrorType.GENERIC_UPLOAD_ERROR }
    };
  }

  const assetUrl = await uploadFileWithSasToken({ file: fileToUpload, sasTokenData });

  if (!assetUrl) {
    return {
      error: { assetName: file.name, errorType: AssetUploadErrorType.GENERIC_UPLOAD_ERROR }
    };
  }

  return {
    assetUrl,
    fileToUpload,
    duration,
    assetType,
    sasAssetName: sasTokenData.data.assetName
  };
};
